<template>
  <Head title="Detail Gallery" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            {
              name: `Gallery`,
              href: route('gallery.index'),
            },
            {
              name: `${gallery.data.title}`,
            },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Detail Card -->
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-images sm:mr-3"></i>
                  Detail Gallery
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <dl class="space-y-4">
                  <div>
                    <dt class="text-sm font-medium text-gray-500">
                      Judul Gallery
                    </dt>
                    <dd class="mt-1 text-sm font-semibold text-gray-900">
                      {{ gallery.data.title }}
                    </dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">Deskripsi</dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ gallery.data.description || "Tidak ada deskripsi" }}
                    </dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">
                      Jumlah Gambar
                    </dt>
                    <dd class="mt-1">
                      <span
                        :class="
                          (Array.isArray(gallery.data.image_path)
                            ? gallery.data.image_path.length
                            : 0) > 0
                            ? 'inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
                            : 'inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full'
                        "
                      >
                        <i class="mr-1 fa-solid fa-images"></i>
                        {{
                          Array.isArray(gallery.data.image_path)
                            ? gallery.data.image_path.length
                            : 0
                        }}
                        foto
                      </span>
                    </dd>
                  </div>
                </dl>

                <!-- Action Buttons -->
                <div
                  class="pt-4 mt-6 border-t border-gray-200"
                  v-if="$page.props.auth.user"
                >
                  <button
                    @click="editDokumen(gallery.data.idgallery)"
                    class="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-indigo-600 transition-colors duration-200 bg-indigo-100 border border-transparent rounded-lg hover:bg-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <i class="mr-2 fa-solid fa-pen"></i>
                    Edit Gallery
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Images Section -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Koleksi Gambar
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Gallery berisi
                    {{
                      Array.isArray(gallery.data.image_path)
                        ? gallery.data.image_path.length
                        : 0
                    }}
                    gambar
                  </p>
                </div>
              </div>
            </div>

            <!-- Images Table -->
            <div
              v-if="
                Array.isArray(gallery.data.image_path) &&
                gallery.data.image_path.length > 0
              "
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[120px]"
                      >
                        Preview
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                      >
                        Nama File
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[120px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(img, idx) in gallery.data.image_path"
                      :key="img + idx"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ idx + 1 }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div
                          class="relative cursor-pointer group"
                          @click="openImageModal(img)"
                        >
                          <img
                            :src="img"
                            :alt="`Gallery Image ${idx + 1}`"
                            class="object-cover w-16 h-16 transition-opacity duration-200 border border-gray-200 rounded-lg shadow-sm group-hover:opacity-75"
                          />
                          <div
                            class="absolute inset-0 flex items-center justify-center transition-all duration-200 bg-black bg-opacity-0 rounded-lg group-hover:bg-opacity-30"
                          >
                            <i
                              class="text-white transition-opacity opacity-0 fa-solid fa-magnifying-glass group-hover:opacity-100"
                            ></i>
                          </div>
                        </div>
                        <!-- Mobile view: Show filename -->
                        <div
                          class="mt-1 text-xs text-gray-500 truncate md:hidden"
                        >
                          {{ img.split("/").pop() }}
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        <div
                          class="max-w-xs truncate"
                          :title="img.split('/').pop()"
                        >
                          {{ img.split("/").pop() }}
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <button
                            @click="editFile(gallery.data.idgallery, idx)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <i class="w-3 h-3 fa-solid fa-pen"></i>
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <button
                            @click="
                              confirmDeleteFile(
                                gallery.data.idgallery,
                                idx,
                                img
                              )
                            "
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <i class="w-3 h-3 fa-solid fa-trash"></i>
                            <span class="hidden ml-1 sm:inline">Hapus</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-else
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="py-12 text-center">
                <div class="flex justify-center">
                  <i class="text-4xl text-gray-400 fa-solid fa-images"></i>
                </div>
                <h3 class="mt-4 text-lg font-medium text-gray-900">
                  Tidak ada gambar
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Gallery ini belum memiliki gambar.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Image Preview Modal -->
        <div
          v-if="showImageModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          @click="closeImageModal"
        >
          <div class="relative max-w-4xl max-h-full p-4">
            <img
              :src="selectedImage"
              alt="Full Size Image"
              class="object-contain max-w-full max-h-full rounded-lg shadow-2xl"
            />
            <button
              @click="closeImageModal"
              class="absolute text-white transition-colors duration-200 top-4 right-4 hover:text-gray-300"
            >
              <i class="text-2xl fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Modals -->
        <EditGalleryModal
          v-model:isOpen="isModalOpen"
          :title="'Edit Gallery'"
          :gallery="datagallery"
        />
        <EditFileModal
          v-model:isOpen="isModalFile"
          :title="'Edit File'"
          :gallery="datagallery"
          :fileIndex="selectedFileIndex"
        />
      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, router } from "@inertiajs/vue3";
import { defineProps, ref, inject } from "vue";
import EditGalleryModal from "@/Components/Gallery/EditGalleryModal.vue";
import EditFileModal from "@/Components/Gallery/EditFileModal.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Swal from "sweetalert2";
const route = inject('route');

const props = defineProps({
  gallery: {
    type: Object,
    required: true,
  },
});

const isModalOpen = ref(false);
const isModalFile = ref(false);
const datagallery = ref(props.gallery?.data || null); // Initialize with current gallery data
const showImageModal = ref(false);
const selectedImage = ref("");
const selectedFileIndex = ref(0);

// Image preview modal functions
const openImageModal = (imageSrc) => {
  selectedImage.value = imageSrc;
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
  selectedImage.value = "";
};

const editDokumen = async (idgallery) => {
  console.log("Edit button clicked for gallery ID:", idgallery);
  console.log("Current gallery props:", props.gallery);

  // Always start with current gallery data as fallback
  if (props.gallery && props.gallery.data) {
    datagallery.value = { ...props.gallery.data }; // Create a copy
    console.log("Using current gallery data:", datagallery.value);
    isModalOpen.value = true;
    return;
  }

  // If no props data, show error
  Swal.fire({
    title: "Error!",
    text: "Data gallery tidak tersedia",
    icon: "error",
    confirmButtonText: "OK",
  });
};

const editFile = async (idgallery, idx) => {
  try {
    const response = await fetch(`/galleryedit/${idgallery}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    datagallery.value = data.gallery;
    selectedFileIndex.value = idx;
    isModalFile.value = true;
  } catch (error) {
    console.error("Error fetching data:", error.message);
    Swal.fire({
      title: "Error!",
      text: "Gagal memuat data file",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

const confirmDeleteFile = async (idgallery, idx, imageUrl) => {
  const fileName = imageUrl.split("/").pop();

  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: `File "${fileName}" akan dihapus permanen!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    await deleteFile(idgallery, idx);
  }
};

const deleteFile = async (idgallery, idx) => {
  try {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus file",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const response = await fetch(`/galleryhapus/${idgallery}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content"),
      },
      body: JSON.stringify({ index: idx }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Update gallery data after deletion
    props.gallery.data.image_path.splice(idx, 1);

    Swal.fire({
      title: "Terhapus!",
      text: "File berhasil dihapus",
      icon: "success",
      confirmButtonText: "OK",
    });

    // Refresh page to update data
    setTimeout(() => {
      router.reload({ only: ["gallery"] });
    }, 1000);
  } catch (error) {
    console.error("Error deleting file:", error.message);
    Swal.fire({
      title: "Error!",
      text: "Gagal menghapus file. Silakan coba lagi.",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};
</script>
